import { inject, Injectable } from '@angular/core';

import {
  GridSettingsConfig,
  TableColumnConfig,
  TableColumnVisibilityConfig,
  UserConfigService
} from 'src/app/services/utility/user-config.service';
import {
  UserConfigTotalConsumptionFilters,
  UserConfigTotalConsumptionFiltersResponse
} from './consumption-analytics.types';
import { map, Observable, ReplaySubject, share } from 'rxjs';
import { resolveDateRangeFromPreset } from 'src/app/shared/components/datepicker/datepicker-preset.utils';
import {
  COLUMNS_ORDER_ELECTRIC_KEY,
  COLUMNS_VISIBILITY_ELECTRIC_KEY,
  COLUMNS_WIDTH_ELECTRIC_KEY,
  GRID_BASE_SETTINGS_KEY
} from './consumption-analytics.constants';

export type TotalConsumptionGridSettings = GridSettingsConfig<UserConfigTotalConsumptionFiltersResponse> & {
  columnWidthElectric: TableColumnConfig;
  columnOrderElectric: TableColumnConfig;
  columnVisibilityElectric: TableColumnVisibilityConfig;
};

@Injectable({ providedIn: 'root' })
export class TotalConsumptionUIService {
  readonly #userConfigService = inject(UserConfigService);

  readonly gridSettings$: Observable<TotalConsumptionGridSettings | null> = this.#userConfigService
    .getGridSettings<UserConfigTotalConsumptionFiltersResponse>(GRID_BASE_SETTINGS_KEY, [
      '_columns_width_electric',
      '_columns_order_electric',
      '_columns_visibility_electric'
    ])
    .pipe(
      map(settings => ({
        ...settings!,
        columnWidthElectric: (settings?.[`${GRID_BASE_SETTINGS_KEY}${COLUMNS_WIDTH_ELECTRIC_KEY}`] ??
          null) as TableColumnConfig,
        columnOrderElectric: (settings?.[`${GRID_BASE_SETTINGS_KEY}${COLUMNS_ORDER_ELECTRIC_KEY}`] ??
          null) as TableColumnConfig,
        columnVisibilityElectric: (settings?.[`${GRID_BASE_SETTINGS_KEY}${COLUMNS_VISIBILITY_ELECTRIC_KEY}`] ??
          null) as TableColumnVisibilityConfig
      })),
      share({ connector: () => new ReplaySubject(1), resetOnComplete: true, resetOnRefCountZero: true })
    );

  persistFilters(filters: UserConfigTotalConsumptionFilters): void {
    this.#userConfigService.saveFilters(GRID_BASE_SETTINGS_KEY, filters);
  }

  loadPersistedFilters(): Observable<{
    filters: UserConfigTotalConsumptionFilters | null;
    pageSize: number | null;
  }> {
    return this.gridSettings$.pipe(
      map(s => {
        const state = s?.filters ?? null;
        return {
          filters: state
            ? {
                ...state,
                dateRange: state.dateRange.presetId
                  ? (resolveDateRangeFromPreset(state.dateRange.presetId) ?? {
                      start: new Date(state.dateRange.start),
                      end: new Date(state.dateRange.end)
                    })
                  : { start: new Date(state.dateRange.start), end: new Date(state.dateRange.end) }
              }
            : null,
          pageSize: s?.pageSize ?? null
        };
      })
    );
  }
}
